import React from "react";
import { motion, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { MagneticButton } from "./primitives/MagneticButton";
import { CountUp } from "./primitives/CountUp";
import { LiquidBackground } from "./primitives/LiquidBackground";
import { HERO, METRICS, COMPLIANCE } from "../data/content";
import { scrollToTarget } from "../lib/scroll";
import { scrollYMV } from "../lib/scrollMotion";

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.35 } },
};
const lineChild = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export const KineticHero = ({ onBook }) => {
  const imgY = useTransform(scrollYMV, [0, 700], [0, 120]);
  const imgScale = useTransform(scrollYMV, [0, 700], [1, 1.12]);
  const [revealed, setRevealed] = React.useState(false);

  React.useEffect(() => {
    const t = setTimeout(() => setRevealed(true), 2600); // fallback if animation is interrupted
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative w-full overflow-hidden pt-8 pb-10 md:pt-10 md:pb-14 lg:pt-14 lg:pb-16" data-testid="hero-section">
      <LiquidBackground />

      {/* Parallax hero image, clipped frame right side */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="pointer-events-none absolute right-0 top-0 hidden h-full w-[42%] lg:block"
      >
        <div className="relative h-full w-full overflow-hidden [clip-path:polygon(18%_0,100%_0,100%_100%,0%_100%)]">
          <img
            src="https://images.unsplash.com/photo-1576242112365-49e5d83e1b31"
            alt="London corporate headquarters at dusk"
            className="h-full w-full object-cover opacity-60"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
        </div>
      </motion.div>

      <div className="relative z-10 mx-auto w-[min(1180px,92vw)]">
        <motion.div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.6 }}
            className="label-eyebrow mb-3 flex items-center gap-2 text-xs text-amber md:mb-4 md:gap-3 md:text-sm lg:mb-5 lg:text-base"
          >
            <span className="h-px w-6 bg-amber/60 lg:w-10" />
            {HERO.eyebrow}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mb-5 flex flex-wrap items-center gap-1.5 md:mb-7 md:gap-2 lg:mb-9"
            data-testid="hero-compliance"
          >
            {COMPLIANCE.map((b) => (
              <span
                key={b}
                className="rounded-full border border-white/12 bg-white/5 px-2.5 py-1 text-[0.65rem] uppercase tracking-[0.12em] text-muted-foreground backdrop-blur-sm md:text-xs lg:px-3.5 lg:py-1.5 lg:text-[0.8rem] lg:tracking-[0.15em]"
              >
                {b}
              </span>
            ))}
          </motion.div>

          <motion.h1
            variants={lineParent}
            initial="hidden"
            animate="show"
            onAnimationComplete={() => setRevealed(true)}
            className="font-display text-[clamp(2.4rem,10vw,3.4rem)] font-500 leading-[1.08] tracking-[-0.02em] md:text-[clamp(3rem,7vw,4.4rem)] lg:text-[clamp(3rem,8vw,7rem)] lg:leading-[1.1]"
          >
            {HERO.lines.map((line) => (
              <span
                key={line}
                className={`relative block pb-[0.08em] ${line === "Safeguarding" ? "z-20 -my-[0.08em]" : "z-0"} ${revealed ? "overflow-visible" : "overflow-hidden"}`}
              >
                <motion.span
                  variants={lineChild}
                  className={`block ${
                    line === "Safeguarding"
                      ? "italic text-gradient-gold pb-[0.35em] pr-[0.12em] -mb-[0.35em] -mr-[0.12em]"
                      : "text-foreground"
                  }`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground md:mt-6 md:text-lg lg:mt-8 lg:text-xl"
          >
            {HERO.paragraph}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.6 }}
            className="mt-6 flex flex-wrap items-center gap-2.5 md:mt-8 md:gap-3 lg:mt-10 lg:gap-4"
          >
            <MagneticButton onClick={onBook} data-testid="hero-book-btn">
              Book a Safety Audit <ArrowUpRight className="h-4 w-4" />
            </MagneticButton>
            <MagneticButton
              variant="ghost"
              onClick={() => scrollToTarget("#practice")}
              data-testid="hero-explore-btn"
            >
              Explore Practice Areas <ArrowRight className="h-4 w-4" />
            </MagneticButton>
          </motion.div>
        </motion.div>

        {/* Metrics strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.7 }}
          className="mt-9 inline-grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl md:mt-11 lg:mt-14 lg:rounded-3xl"
          data-testid="hero-metrics"
        >
          {METRICS.map((m) => (
            <div key={m.label} className="bg-ink/40 px-5 py-3.5 md:px-8 md:py-4 lg:px-12 lg:py-5">
              <div className="font-display text-3xl leading-none text-amber md:text-4xl lg:text-6xl">
                {m.prefix || ""}
                <CountUp end={m.value} suffix={m.suffix} zero={m.zero} />
              </div>
              <p className="mt-1.5 text-xs leading-snug text-muted-foreground md:text-sm lg:mt-2 lg:text-base">{m.label}</p>
            </div>
          ))}
        </motion.div>
      </div>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-muted-foreground lg:flex"
      >
        <span className="text-[0.65rem] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="h-8 w-px bg-gradient-to-b from-amber to-transparent"
        />
      </motion.div>
    </section>
  );
};
